import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { delay } from 'rxjs/operators';
import {
  LLMConnectionTestResult,
  LLMCustomerAccess,
  LLMMyAccess,
  LLMProvider,
  LLMSelectableProvider,
  LLMProviderSettings,
  LLMUsageSummary
} from '../Model/AI/LLMProvider';

@Injectable({
  providedIn: 'root'
})
export class LlmProviderService {

  private readonly jsonOptions = {
    headers: new HttpHeaders().append('Content-Type', 'application/json')
  };

  constructor(private http: HttpClient) {
  }

  getSettings(): Observable<LLMProviderSettings[]> {
    return this.http.get<LLMProviderSettings[]>('api/LLMProvider/GetSettings', this.jsonOptions);
  }

  saveSettings(settings: LLMProviderSettings): Observable<void> {
    return this.http.post<void>('api/LLMProvider/SaveSettings', settings, this.jsonOptions);
  }

  deleteSettings(id: string): Observable<void> {
    return this.http.post<void>(`api/LLMProvider/DeleteSettings?settingsId=${id}`, null, this.jsonOptions);
  }

  testConnection(settings: LLMProviderSettings): Observable<LLMConnectionTestResult> {
    return this.http.post<LLMConnectionTestResult>('api/LLMProvider/TestSettings', settings, this.jsonOptions);
  }

  getSelectableProviders(customerId: string): Observable<LLMSelectableProvider[]> {
    return this.http.get<LLMSelectableProvider[]>(
      `api/LLMProvider/GetSelectableProviders?customerId=${customerId}`, this.jsonOptions);
  }

  getMyAccess(customerId: string): Observable<LLMMyAccess[]> {
    return this.http.get<LLMMyAccess[]>(
      `api/LLMProvider/GetMyAccess?customerId=${customerId}`, this.jsonOptions);
  }

  saveMyAccess(customerId: string, access: LLMMyAccess): Observable<void> {
    return this.http.post<void>(
      `api/LLMProvider/SaveMyAccess?customerId=${customerId}`, access, this.jsonOptions);
  }

  deleteMyAccess(customerId: string, accessId: string): Observable<void> {
    return this.http.post<void>(
      `api/LLMProvider/DeleteMyAccess?customerId=${customerId}&accessId=${accessId}`, null, this.jsonOptions);
  }

  testMyAccess(customerId: string, access: LLMMyAccess): Observable<LLMConnectionTestResult> {
    return this.http.post<LLMConnectionTestResult>(
      `api/LLMProvider/TestMyAccess?customerId=${customerId}`, access, this.jsonOptions);
  }

  getCustomerAccess(): Observable<LLMCustomerAccess[]> {
    return this.http.get<LLMCustomerAccess[]>('api/LLMProvider/GetCustomerAccess', this.jsonOptions);
  }

  // Usage figures are not recorded yet; the panel shows sample data until that lands.
  getUsage(days: number): Observable<LLMUsageSummary> {
    const perDay = Array.from({ length: days }, (_, i) => {
      const date = new Date();
      date.setDate(date.getDate() - (days - 1 - i));
      const requests = 400 + Math.round(Math.sin(i / 3) * 180) + i * 12;
      return {
        day: date.toISOString().slice(0, 10),
        requests,
        spend: Math.round(requests * 0.018 * 100) / 100
      };
    });

    return of({
      totalRequests: perDay.reduce((sum, p) => sum + p.requests, 0),
      totalSpend: Math.round(perDay.reduce((sum, p) => sum + p.spend, 0) * 100) / 100,
      failureRate: 0.021,
      ownCredentialShare: 0.34,
      perDay,
      perProvider: [
        { provider: LLMProvider.AzureOpenAI, requests: 8420, spend: 151.56 },
        { provider: LLMProvider.OpenAI, requests: 3110, spend: 88.13 },
        { provider: LLMProvider.OpenRouter, requests: 1240, spend: 12.4 },
        { provider: LLMProvider.Deepseek, requests: 260, spend: 1.04 }
      ],
      topCustomers: [
        { customerName: 'Muster Handel GmbH', requests: 4120, spend: 0, ownCredentials: true },
        { customerName: 'Bau & Technik SE', requests: 3380, spend: 60.84, ownCredentials: false },
        { customerName: 'Elektro Süd KG', requests: 2210, spend: 39.78, ownCredentials: false },
        { customerName: 'Technik Nord AG', requests: 1890, spend: 0, ownCredentials: true },
        { customerName: 'Sanitär West GmbH', requests: 1430, spend: 25.74, ownCredentials: false }
      ]
    }).pipe(delay(250));
  }
}
